import District from './District';
import Result from './Result';
import School from './School';
import Specialization from './Specialization';
import Teacher from './Teacher';
import { Dayjs } from 'dayjs';

class Student {
	id: number;
	personalTitle: string;
	firstName: string;
	secondName: string;
	lastName: string;
	schoolClass: number;
	schoolClassText: string;
	email: string;
	phone: string;
	parentName: string;
	parentEmail: string;
	approved: boolean;
	emailVerified: boolean;
	specialCertificate: boolean;
	country: string;
	city: string;
	zipCode: string;
	street: string;
	building: string;
	apartment: string;
	district: District;
	school: School;
	specialization: Specialization;
	teachers: Teacher[];
	result: Result;
	stage3WorkCode: string | null;
	/** Meta skanów opinii (kolejność wgrania). */
	opinionScan: {
		hasFile: boolean;
		files: Array<{
			id: number;
			originalName: string;
			storedName: string;
			storageBackend: string;
			driveFileId: string | null;
			previewUrl: string | null;
		}>;
		/** Pierwszy plik — kompatybilność. */
		storageBackend?: string;
		driveFileId?: string | null;
		previewUrl?: string | null;
		originalName?: string;
		storedName?: string;
	} | null;

	certEmail: string;
	certStreet: string;
	certBuilding: string;
	certApartment: string;
	certZipCode: string;
	certCity: string;
	declarationsIds: [];
	declarations: [];
	declarationsDetails: any[];
	birthdate: Dayjs | null;
	birthdatePlace: string | null;
	topic: any;
	notes: string;

	constructor(
		id: number,
		personalTitle: string,
		firstName: string,
		secondName: string,
		lastName: string,
		schoolClass: number,
		schoolClassText: string,
		email: string,
		phone: string,
		parentName: string,
		parentEmail: string,
		approved: boolean,
		emailVerified: boolean,
		specialCertificate: boolean,
		country: string,
		city: string,
		zipCode: string,
		street: string,
		building: string,
		apartment: string,
		district: District,
		school: School,
		specialization: Specialization,
		teachers: Teacher[],
		result: Result,
		stage3WorkCode: string | null,
		opinionScan: Student['opinionScan'],
		certEmail: string,
		certStreet: string,
		certBuilding: string,
		certApartment: string,
		certZipCode: string,
		certCity: string,
		declarationsIds: [],
		declarations: [],
		declarationsDetails: any[],
		birthdate: Dayjs | null,
		birthdatePlace: string | null,
		topic: any,
		notes: string,
	) {
		this.id = id;
		this.personalTitle = personalTitle;
		this.firstName = firstName;
		this.secondName = secondName;
		this.lastName = lastName;
		this.schoolClass = schoolClass;
		this.schoolClassText = schoolClassText;
		this.email = email;
		this.phone = phone;
		this.parentName = parentName;
		this.parentEmail = parentEmail;
		this.approved = approved;
		this.emailVerified = emailVerified;
		this.specialCertificate = specialCertificate;
		this.country = country;
		this.city = city;
		this.zipCode = zipCode;
		this.street = street;
		this.building = building;
		this.apartment = apartment;
		this.district = district;
		this.school = school;
		this.specialization = specialization;
		this.teachers = teachers;
		this.result = result;
		this.stage3WorkCode = stage3WorkCode;
		this.opinionScan = opinionScan;
		this.certEmail = certEmail;
		this.certStreet = certStreet;
		this.certBuilding = certBuilding;
		this.certApartment = certApartment;
		this.certZipCode = certZipCode;
		this.certCity = certCity;
		this.declarationsIds = declarationsIds;
		this.declarations = declarations;
		this.declarationsDetails = declarationsDetails;
		this.birthdate = birthdate;
		this.birthdatePlace = birthdatePlace;
		this.topic = topic;
		this.notes = notes;
	}

	public static fromApiResponse(data: any) {
		return new Student(
			data.id ? data.id : 0,
			data.personal_title ? data.personal_title : '',
			data.first_name ? data.first_name : '',
			data.second_name ? data.second_name : '',
			data.last_name ? data.last_name : '',
			data.class ? data.class : 1,
			data.class_text ? data.class_text : '',
			data.email ? data.email : '',
			data.phone ? data.phone : '',
			data.parent_name ? data.parent_name : '',
			data.parent_email ? data.parent_email : '',
			data.approved === true || data.approved === 1 || data.approved === '1',
			data.email_verified !== false && data.email_verified !== 0 && data.email_verified !== '0',
			data.special_certificate === true || data.special_certificate === 1 || data.special_certificate === '1',
			data.country ? data.country : '',
			data.city ? data.city : '',
			data.zip_code ? data.zip_code : '',
			data.street ? data.street : '',
			data.building ? data.building : '',
			data.apartment ? data.apartment : '',
			District.fromApiResponse(data.district ? data.district : {}),
			School.fromApiResponse(data.school ? data.school : {}),
			Specialization.fromApiResponse(data.specialization ? data.specialization : {}),
			data.teachers ? data.teachers.map((t: any) => Teacher.fromApiResponse(t)) : [],
			Result.fromApiResponse(data.results ? data.results : {}),
			data.stage3_work_code ? data.stage3_work_code : null,
			data.opinion_scan && data.opinion_scan.hasFile
				? (() => {
						const rawFiles = Array.isArray(data.opinion_scan.files) ? data.opinion_scan.files : [];
						const files =
							rawFiles.length > 0
								? rawFiles.map((f: any) => ({
										id: Number(f.id) || 0,
										originalName: f.originalName || f.original_name || '',
										storedName: f.storedName || f.stored_name || '',
										storageBackend: f.storageBackend || f.storage_backend || 'local',
										driveFileId: f.driveFileId || f.drive_file_id || null,
										previewUrl: f.previewUrl || f.preview_url || null,
									}))
								: [
										{
											id: 0,
											originalName: data.opinion_scan.originalName || data.opinion_scan.original_name || '',
											storedName: data.opinion_scan.storedName || data.opinion_scan.stored_name || '',
											storageBackend: data.opinion_scan.storageBackend || data.opinion_scan.storage_backend || 'local',
											driveFileId: data.opinion_scan.driveFileId || data.opinion_scan.drive_file_id || null,
											previewUrl: data.opinion_scan.previewUrl || data.opinion_scan.preview_url || null,
										},
									];
						const first = files[0];
						return {
							hasFile: true,
							files,
							storageBackend: first?.storageBackend,
							driveFileId: first?.driveFileId ?? null,
							previewUrl: first?.previewUrl ?? null,
							originalName: first?.originalName,
							storedName: first?.storedName,
						};
					})()
				: null,
			data.cert_email ? data.cert_email : '',
			data.cert_street ? data.cert_street : '',
			data.cert_building ? data.cert_building : '',
			data.cert_apartment ? data.cert_apartment : '',
			data.cert_zip_code ? data.cert_zip_code : '',
			data.cert_city ? data.cert_city : '',
			data.declarations ? data.declarations.map((d: any) => d.id) : [],
			data.declarations ? data.declarations.map((d: any) => d.short_title) : [],
			data.declarations
				? data.declarations.map((d: any) => ({
						id: d.id,
						declaration: d.declaration ?? d.short_title ?? '',
						required: d.required,
						short_title: d.short_title,
					}))
				: [],
			data.birthday ? data.birthday : null,
			data.birthday_place ? data.birthday_place : null,
			{
				category: data.stage_three_b_oral_category,
				topic: data.stage_three_b_oral_topic,
				linguisticTopic: data.stage_three_b_oral_linguistic_topic,
			},
			data.notes ? data.notes : '',
		);
	}
}

export default Student;
